import { ArticleCardLink } from '@/components/site/article-card-link';
import {
    FadeCoverGallery,
    type FadeCoverSlide,
} from '@/components/site/fade-cover-gallery';
import type { ArticleCardProps } from '@/lib/article-card';
import { cn } from '@/lib/utils';

/** Resolve ordered cover URLs for a card (gallery or single cover). */
export function cardCoverSrcs(
    article: Pick<ArticleCardProps, 'cover' | 'covers'>,
): string[] {
    if (article.covers?.length) return article.covers.filter(Boolean);
    return article.cover ? [article.cover] : [];
}

function toSlides(srcs: string[], alt: string): FadeCoverSlide[] {
    return srcs.map((src, i) => ({
        id: `${i}-${src}`,
        src,
        alt: i === 0 ? alt : '',
    }));
}

/**
 * Linked article cover: fades through hero gallery images when present.
 * Dot controls sit above the link overlay so they stay clickable.
 */
export function LinkedCardCover({
    article,
    className,
    imageClassName,
    width,
    height,
    sizes = '(max-width: 640px) 100vw, 40vw',
    fetchPriority,
    loading,
    showControls = true,
}: {
    article: Pick<ArticleCardProps, 'slug' | 'redirect' | 'title' | 'cover' | 'covers'>;
    className?: string;
    imageClassName?: string;
    width?: number;
    height?: number;
    sizes?: string;
    fetchPriority?: 'high' | 'low' | 'auto';
    loading?: 'eager' | 'lazy';
    showControls?: boolean;
}) {
    const srcs = cardCoverSrcs(article);
    if (srcs.length === 0) return null;

    return (
        <FadeCoverGallery
            slides={toSlides(srcs, article.title)}
            className={cn('image-frame', className)}
            imageClassName={imageClassName}
            width={width}
            height={height}
            sizes={sizes}
            fetchPriority={fetchPriority}
            loading={loading}
            showControls={showControls}
            overlay={
                <ArticleCardLink
                    article={article}
                    className="absolute inset-0 z-[1]"
                >
                    <span className="sr-only">{article.title}</span>
                </ArticleCardLink>
            }
        />
    );
}
